/**
 * Placeholders in an announcement's title or body (TNG-95), as the composer
 * checks and previews them.
 *
 * The mirror of functions/python/app/application/service/
 * announcement_placeholders.py, which is what fills them in when the message
 * goes out. Both are run against the same vectors
 * (functions/python/tests/fixtures/announcement_placeholders/vectors.json),
 * and a backend test holds the key list below to the backend's. Syntax:
 *
 *  - `{key}` fills in the recipient's value; `{key|text}` uses `text` when
 *    they have none (at most 100 characters, no braces).
 *  - `{{` and `}}` are literal braces.
 *  - Anything else in braces is left as written, so older text is unchanged.
 *  - A lowercase `{key}` that names no placeholder is refused, since it is
 *    almost always a typo.
 */

export const MAX_FALLBACK_LENGTH = 100;
export const ELLIPSIS = "…";

/**
 * Every placeholder, in chip order. `fallback` is what a recipient without a
 * value sees when the organiser wrote no inline fallback.
 */
export const PLACEHOLDERS = [
  { key: "first_name", label: "First name", fallback: "there" },
  { key: "full_name", label: "Full name", fallback: "there" },
  { key: "track", label: "Track", fallback: "TBA" },
  { key: "room", label: "Room", fallback: "TBA" },
  { key: "question", label: "Theme question", fallback: "TBA" },
  { key: "champion", label: "Champion", fallback: "TBA" },
  // TNG-94: the table in the dinner seating the announcement names.
  { key: "dinner_table", label: "Dinner table", fallback: "TBA" },
] as const;

export type PlaceholderKey = (typeof PLACEHOLDERS)[number]["key"];
export type PlaceholderValues = Partial<
  Record<PlaceholderKey, string | null | undefined>
>;

const BY_KEY = new Map<string, (typeof PLACEHOLDERS)[number]>(
  PLACEHOLDERS.map((spec) => [spec.key, spec]),
);

export function isPlaceholderKey(key: string): key is PlaceholderKey {
  return BY_KEY.has(key);
}

/** One run of the source text, as written, and what it means. */
export type Segment =
  | { kind: "text"; raw: string; text: string }
  | {
      kind: "placeholder";
      raw: string;
      key: PlaceholderKey;
      fallback: string | null;
    }
  | { kind: "invalid"; raw: string; problem: string };

const TOKEN = /\{([a-z_]+)(?:\|([^{}]*))?\}/y;

/**
 * The template split into runs, keeping the raw text of each so a list can
 * show exactly what was typed with the placeholders highlighted. An invalid
 * placeholder is its own segment carrying the reason; parseTemplate turns it
 * into an error or, leniently, text.
 */
export function segments(text: string): Segment[] {
  const out: Segment[] = [];
  let raw = "";
  let literal = "";
  const flush = () => {
    if (raw) out.push({ kind: "text", raw, text: literal });
    raw = "";
    literal = "";
  };
  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (char === "{") {
      if (text.startsWith("{{", i)) {
        raw += "{{";
        literal += "{";
        i += 2;
        continue;
      }
      TOKEN.lastIndex = i;
      const match = TOKEN.exec(text);
      if (match) {
        const [whole, key, fallback] = match;
        let problem: string | null = null;
        if (!isPlaceholderKey(key)) {
          problem = `{${key}} is not a placeholder. Use one of ${PLACEHOLDERS.map((spec) => `{${spec.key}}`).join(", ")}, or write {{ and }} for literal braces.`;
        } else if (
          fallback !== undefined &&
          codePoints(fallback) > MAX_FALLBACK_LENGTH
        ) {
          problem = `The fallback text for {${key}} is longer than ${MAX_FALLBACK_LENGTH} characters.`;
        }
        flush();
        out.push(
          problem === null
            ? {
                kind: "placeholder",
                raw: whole,
                key: key as PlaceholderKey,
                fallback: fallback ?? null,
              }
            : { kind: "invalid", raw: whole, problem },
        );
        i += whole.length;
        continue;
      }
      raw += "{";
      literal += "{";
      i += 1;
      continue;
    }
    if (char === "}") {
      const pair = text.startsWith("}}", i);
      raw += pair ? "}}" : "}";
      literal += "}";
      i += pair ? 2 : 1;
      continue;
    }
    raw += char;
    literal += char;
    i += 1;
  }
  flush();
  return out;
}

export interface Template {
  parts: (string | { key: PlaceholderKey; fallback: string | null })[];
}

export class PlaceholderError extends Error {}

export function parseTemplate(text: string, strict = true): Template {
  const parts: Template["parts"] = [];
  let literal = "";
  for (const segment of segments(text)) {
    if (segment.kind === "placeholder") {
      if (literal) parts.push(literal);
      literal = "";
      parts.push({ key: segment.key, fallback: segment.fallback });
    } else if (segment.kind === "invalid") {
      if (strict) throw new PlaceholderError(segment.problem);
      // Lenient: the invalid token is text, brace by brace, exactly as the
      // backend reads a template stored before the check existed.
      literal += segment.raw;
    } else {
      literal += segment.text;
    }
  }
  if (literal) parts.push(literal);
  return { parts };
}

/** Why this title or body cannot be sent, or null. */
export function templateProblem(text: string): string | null {
  const invalid = segments(text).find((segment) => segment.kind === "invalid");
  return invalid && invalid.kind === "invalid" ? invalid.problem : null;
}

export function templateKeys(text: string): Set<PlaceholderKey> {
  return new Set(
    segments(text).flatMap((segment) =>
      segment.kind === "placeholder" ? [segment.key] : [],
    ),
  );
}

export function isPersonalised(text: string): boolean {
  return templateKeys(text).size > 0;
}

/** Length in code points, the unit both sides count in. */
export function codePoints(text: string): number {
  return Array.from(text).length;
}

/**
 * The characters both sides treat as whitespace: Unicode's White_Space
 * property, spelled out because JavaScript's \s and Python's str.split()
 * disagree. The backend's WHITESPACE is the same set, and the shared vectors
 * list it.
 */
export const WHITESPACE: ReadonlySet<string> = new Set([
  "\t",
  "\n",
  "\x0b",
  "\x0c",
  "\r",
  " ",
  "\x85",
  "\xa0",
  "\u1680",
  ...Array.from({ length: 11 }, (_, i) => String.fromCodePoint(0x2000 + i)),
  "\u2028",
  "\u2029",
  "\u202f",
  "\u205f",
  "\u3000",
]);

const ZWNJ = "\u200c";
const ZWJ = "\u200d";
const FORMAT_OR_CONTROL = /^[\p{Cf}\p{Cc}]$/u;
const COMBINING_MARK = /^\p{M}$/u;

function isTag(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code >= 0xe0020 && code <= 0xe007f;
}

/**
 * A character a value loses before it is inserted: format characters (Cf,
 * which include the bidi controls and U+FEFF) and control characters (Cc)
 * that are not whitespace. ZWNJ, ZWJ and emoji tag characters are kept.
 */
function removed(char: string): boolean {
  if (WHITESPACE.has(char) || char === ZWNJ || char === ZWJ || isTag(char))
    return false;
  return FORMAT_OR_CONTROL.test(char);
}

function split(text: string): string[] {
  const words: string[] = [];
  let current = "";
  for (const char of text) {
    if (WHITESPACE.has(char)) {
      if (current) words.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  if (current) words.push(current);
  return words;
}

function strip(text: string): string {
  const chars = Array.from(text);
  let start = 0;
  let end = chars.length;
  while (start < end && WHITESPACE.has(chars[start])) start += 1;
  while (end > start && WHITESPACE.has(chars[end - 1])) end -= 1;
  return chars.slice(start, end).join("");
}

/**
 * A recipient's value as it is inserted, or null when it is blank: format
 * and control characters removed, whitespace runs collapsed, ends trimmed.
 */
export function cleanValue(value: string | null | undefined): string | null {
  if (value == null) return null;
  const kept = Array.from(value)
    .filter((char) => !removed(char))
    .join("");
  return split(kept).join(" ") || null;
}

function isExtender(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return (
    char === ZWJ ||
    (code >= 0xfe00 && code <= 0xfe0f) ||
    (code >= 0xe0100 && code <= 0xe01ef) ||
    (code >= 0x1f3fb && code <= 0x1f3ff) ||
    isTag(char) ||
    COMBINING_MARK.test(char)
  );
}

function isRegionalIndicator(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code >= 0x1f1e6 && code <= 0x1f1ff;
}

function splitsAGrapheme(chars: string[], cut: number): boolean {
  if (cut <= 0 || cut >= chars.length) return false;
  const before = chars[cut - 1];
  const after = chars[cut];
  if (isExtender(after) || before === ZWJ) return true;
  if (isRegionalIndicator(after)) {
    let run = 0;
    while (cut - 1 - run >= 0 && isRegionalIndicator(chars[cut - 1 - run]))
      run += 1;
    return run % 2 === 1;
  }
  return false;
}

/**
 * `text` cut to `maxLength` code points, ending in "…" when cut, backed off
 * to a grapheme boundary. See `truncate` in the backend module.
 */
export function truncate(text: string, maxLength: number): string {
  const chars = Array.from(text);
  if (chars.length <= maxLength) return text;
  let cut = maxLength - 1;
  while (splitsAGrapheme(chars, cut)) cut -= 1;
  return strip(chars.slice(0, cut).join("")) + ELLIPSIS;
}

function join(
  template: Template,
  values: PlaceholderValues,
  useFallbacks: boolean,
): string {
  return template.parts
    .map((part) => {
      if (typeof part === "string") return part;
      const value = cleanValue(values[part.key]);
      if (value !== null) return value;
      if (useFallbacks && part.fallback !== null) return part.fallback;
      return BY_KEY.get(part.key)?.fallback ?? "";
    })
    .join("");
}

function filledIn(template: Template, values: PlaceholderValues): string {
  return (
    strip(join(template, values, true)) || strip(join(template, values, false))
  );
}

/**
 * The text one recipient sees, at most `maxLength` code points. See `render`
 * in the backend module for the rules.
 */
export function render(
  template: Template,
  values: PlaceholderValues,
  maxLength: number,
): string {
  return truncate(filledIn(template, values), maxLength);
}

/** Lenient parse and render in one, for text that may predate the check. */
export function renderText(
  text: string,
  values: PlaceholderValues,
  maxLength: number,
): string {
  return render(parseTemplate(text, false), values, maxLength);
}

/** Whether filling in `values` would cut the text. */
export function wouldTruncate(
  text: string,
  values: PlaceholderValues,
  maxLength: number,
): boolean {
  return codePoints(filledIn(parseTemplate(text, false), values)) > maxLength;
}

export function firstName(name: string | null | undefined): string | null {
  return cleanValue(name)?.split(" ")[0] ?? null;
}

/** The `{key}` text a chip inserts. */
export function placeholderToken(key: PlaceholderKey): string {
  return `{${key}}`;
}
